// Same-domain org gate: an authenticated user with no org is held on /join-team
// when a paying org already exists for their email domain, unless they opted
// to create a separate workspace.

export type ExistingOrgCheck = {exists: boolean; orgEmail: string | null; ssoEnforced: boolean};
export type OrgGateUrls = {apiBaseUrl: string; propelAuthUrl: string};
export type OrgGateDecision = 'proceed' | 'blocked';
export type OrgGateResult = {decision: OrgGateDecision; check: ExistingOrgCheck};

export type OrgGateAuth = {
  accessToken: string;
  user: {email: string; hasPassword?: boolean};
  orgHelper: {getOrgs: () => unknown[]};
};

export type FetchLike = (
  _input: string,
  _init?: {headers?: Record<string, string>; signal?: AbortSignal}
) => Promise<{ok: boolean; status?: number; json: () => Promise<unknown>}>;

// A stalled probe must not hold the auth middleware; a timeout counts as unknown.
export const SSO_PROBE_TIMEOUT_MS = 5000;

export const JOIN_TEAM_PATH = '/join-team';
export const NO_ACCOUNT_PATH = '/no-account';

// Auth pages own their own navigation (e.g. /logout must clear the session
// first), so org bootstrap never redirects away from them.
const BOOTSTRAP_REDIRECT_EXEMPT = [
  '/login',
  '/signup',
  '/auth/callback',
  '/logout',
  JOIN_TEAM_PATH,
  NO_ACCOUNT_PATH,
];

export const isBootstrapRedirectExempt = (pathname: string): boolean =>
  BOOTSTRAP_REDIRECT_EXEMPT.some(path => pathname === path || pathname.startsWith(`${path}/`));

const SEPARATE_ORG_OPT_IN_KEY = 'vector_create_separate_org';
const SIGNUP_ALLOWED_KEY = 'vector_signup_allowed';
const EXISTING_ORG_CHECK_KEY = 'vector_existing_org_check';
const NO_EXISTING_ORG: ExistingOrgCheck = {exists: false, orgEmail: null, ssoEnforced: false};

// An SSO org for the domain always blocks: a second org there breaks SSO for everyone.
export const resolveOrgGate = (
  check: ExistingOrgCheck,
  optedIntoSeparateOrg: boolean
): OrgGateDecision => {
  if (check.ssoEnforced) return 'blocked';
  return check.exists && !optedIntoSeparateOrg ? 'blocked' : 'proceed';
};

// Both onboarding markers are bound to the user's email so a second account in
// the same browser cannot inherit them.
type SignupIntent = {email: string | null; at: number};

// A pre-auth intent is only valid for the Google round trip itself.
const SIGNUP_INTENT_TTL_MS = 15 * 60 * 1000;

const readSignupIntent = (): SignupIntent | null => {
  try {
    const raw = localStorage.getItem(SIGNUP_ALLOWED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {email?: unknown; at?: unknown};
    return {
      email: typeof parsed.email === 'string' ? parsed.email : null,
      at: typeof parsed.at === 'number' ? parsed.at : 0,
    };
  } catch (_) {
    return null;
  }
};

const writeSignupIntent = (intent: SignupIntent): void => {
  try {
    localStorage.setItem(SIGNUP_ALLOWED_KEY, JSON.stringify(intent));
  } catch (_) {
    /* noop */
  }
};

const isFreshAnonymousIntent = (intent: SignupIntent | null, now: number): boolean =>
  !!intent && intent.email === null && now - intent.at <= SIGNUP_INTENT_TTL_MS;

// Set by /signup before a Google redirect. Without it, a social-login user with
// no org came through the /login Google button and must not get an org.
export const markSignupAllowed = (email?: string, now = Date.now()): void => {
  writeSignupIntent({email: email?.trim() || null, at: now});
};

// Called once identity is known (auth callback): a fresh pre-auth intent is
// bound to this user; a stale or foreign one is left to fail the check.
export const bindSignupAllowed = (email: string, now = Date.now()): void => {
  const intent = readSignupIntent();
  if (isFreshAnonymousIntent(intent, now)) writeSignupIntent({email, at: now});
};

// A partner deep-link is an intended signup channel. The pre-auth marker only
// covers the visit that followed the link, so a signed-in org-less arrival on a
// partner route (e.g. a retry) is accepted as the signal instead.
const PARTNER_LANDING_PATHS = [
  '/partner-integrations/',
  '/integrations/common_room',
  '/integrations/userled',
];

export const isPartnerLandingPath = (pathname: string): boolean =>
  PARTNER_LANDING_PATHS.some(path => pathname === path || pathname.startsWith(path));

export const allowPartnerSignup = (pathname: string, email: string): boolean => {
  if (!isPartnerLandingPath(pathname)) return false;
  markSignupAllowed(email);
  return true;
};

export const clearSignupAllowed = (): void => {
  try {
    localStorage.removeItem(SIGNUP_ALLOWED_KEY);
  } catch (_) {
    /* noop */
  }
};

// Login methods whose users can only exist via our own signup endpoint or an
// admin. Anything else (social, unknown) needs the /signup marker.
const TRUSTED_LOGIN_METHODS = new Set([
  'password',
  'magic_link',
  'email_confirmation_link',
  'saml_sso',
  'impersonation',
  'generated_from_backend_api',
]);

// Reads the login_method claim from the PropelAuth access token. Unverified on
// purpose: this only steers routing, org membership is enforced server-side.
export const readLoginMethod = (accessToken: string): string | null => {
  try {
    const segment = accessToken.split('.')[1] ?? '';
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded)) as {login_method?: {login_method?: unknown}};
    const method = claims.login_method?.login_method;
    return typeof method === 'string' ? method : null;
  } catch (_) {
    return null;
  }
};

export const isUnauthorizedSignup = (
  auth: Pick<OrgGateAuth, 'accessToken' | 'orgHelper' | 'user'>
): boolean => {
  if (auth.orgHelper.getOrgs().length > 0) return false;
  // Only our signup endpoint creates users with a password; social logins never do.
  if (auth.user.hasPassword === true) return false;
  const method = readLoginMethod(auth.accessToken);
  if (method && TRUSTED_LOGIN_METHODS.has(method)) return false;
  return readSignupIntent()?.email !== auth.user.email;
};

// localStorage so the choice survives the hard navigation that re-runs org bootstrap.
export const hasSeparateOrgOptIn = (email: string): boolean => {
  try {
    return localStorage.getItem(SEPARATE_ORG_OPT_IN_KEY) === email;
  } catch (_) {
    return false;
  }
};

export const setSeparateOrgOptIn = (email: string): void => {
  try {
    localStorage.setItem(SEPARATE_ORG_OPT_IN_KEY, email);
  } catch (_) {
    /* noop */
  }
};

export const clearSeparateOrgOptIn = (): void => {
  try {
    localStorage.removeItem(SEPARATE_ORG_OPT_IN_KEY);
  } catch (_) {
    /* noop */
  }
};

// Logout and failed onboarding must not leave markers for the next account.
export const clearOnboardingMarkers = (): void => {
  clearSignupAllowed();
  clearSeparateOrgOptIn();
};

// sessionStorage cache keyed by email so middleware and org bootstrap share one lookup per tab.
// A negative SSO answer only lives this long before createOrg needs a fresh probe;
// a positive one is kept for the tab.
export const NEGATIVE_SSO_CACHE_TTL_MS = 5 * 60 * 1000;

export const readCachedExistingOrgCheck = (
  email: string,
  now = Date.now()
): ExistingOrgCheck | null => {
  try {
    const raw = sessionStorage.getItem(EXISTING_ORG_CHECK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {email?: unknown; check?: unknown; at?: unknown};
    if (parsed.email !== email) return null;
    // Records from before the SSO flag existed cannot vouch for it.
    const flag = (parsed.check as {ssoEnforced?: unknown} | null)?.ssoEnforced;
    if (typeof flag !== 'boolean' || typeof parsed.at !== 'number') return null;
    if (!flag && now - parsed.at > NEGATIVE_SSO_CACHE_TTL_MS) return null;
    return parseExistingOrgCheck(parsed.check);
  } catch (_) {
    return null;
  }
};

export const cacheExistingOrgCheck = (
  email: string,
  check: ExistingOrgCheck,
  now = Date.now()
): void => {
  try {
    sessionStorage.setItem(EXISTING_ORG_CHECK_KEY, JSON.stringify({email, check, at: now}));
  } catch (_) {
    /* noop */
  }
};

const parseExistingOrgCheck = (data: unknown): ExistingOrgCheck => {
  if (!data || typeof data !== 'object') return NO_EXISTING_ORG;
  const {exists, orgEmail, ssoEnforced} = data as {
    exists?: unknown;
    orgEmail?: unknown;
    ssoEnforced?: unknown;
  };
  return {
    exists: exists === true,
    orgEmail: typeof orgEmail === 'string' ? orgEmail : null,
    ssoEnforced: ssoEnforced === true,
  };
};

// PropelAuth's public SSO lookup: 200 means one SAML org owns the domain and
// 409 means several already do. Returns null when the answer is unknown.
export async function fetchSsoOrgForDomain(
  propelAuthUrl: string,
  domain: string,
  fetchImpl: FetchLike = fetch,
  timeoutMs = SSO_PROBE_TIMEOUT_MS
): Promise<boolean | null> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | null = null;
  const deadline = new Promise<null>(resolve => {
    timer = setTimeout(() => {
      controller.abort();
      resolve(null);
    }, timeoutMs);
  });
  try {
    const params = new URLSearchParams({domain});
    const request = fetchImpl(`${propelAuthUrl}/api/fe/v3/login/check?${params}`, {
      signal: controller.signal,
    }).then(response => {
      if (response.ok || response.status === 409) return true;
      if (response.status === 404) return false;
      return null;
    });
    return await Promise.race([request, deadline]);
  } catch (_) {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// Raw fetch on purpose: apiFetch resolves an active org, which is what is missing here.
// Returns null on any failure so callers fail open (org gets created, as before).
export async function fetchExistingOrgForDomain(
  apiBaseUrl: string,
  accessToken: string,
  fetchImpl: FetchLike = fetch
): Promise<ExistingOrgCheck | null> {
  try {
    const response = await fetchImpl(`${apiBaseUrl}/billing/existing-org-check`, {
      headers: {Authorization: `Bearer ${accessToken}`},
    });
    if (!response.ok) return null;
    return parseExistingOrgCheck(await response.json());
  } catch (_) {
    return null;
  }
}

export async function evaluateOrgGate(
  auth: OrgGateAuth,
  urls: OrgGateUrls,
  fetchImpl?: FetchLike
): Promise<OrgGateResult> {
  if (auth.orgHelper.getOrgs().length > 0) {
    return {decision: 'proceed', check: NO_EXISTING_ORG};
  }
  const email = auth.user.email;
  const domain = email.split('@')[1]?.toLowerCase() ?? '';
  let check = readCachedExistingOrgCheck(email);
  if (!check) {
    // One retry: an unknown SSO answer must not let a sibling org slip through.
    const probeSso = async (): Promise<boolean | null> => {
      if (!domain) return false;
      const first = await fetchSsoOrgForDomain(urls.propelAuthUrl, domain, fetchImpl);
      return first ?? fetchSsoOrgForDomain(urls.propelAuthUrl, domain, fetchImpl);
    };
    const [sso, paying] = await Promise.all([
      probeSso(),
      fetchExistingOrgForDomain(urls.apiBaseUrl, auth.accessToken, fetchImpl),
    ]);
    // Both lookups unknown: fail open, as before.
    if (sso === null && !paying) return {decision: 'proceed', check: NO_EXISTING_ORG};
    // SSO still unknown: fail closed. Nothing is cached, so /join-team re-checks on mount.
    if (sso === null) return {decision: 'blocked', check: {...paying!, ssoEnforced: false}};
    check = {
      exists: paying?.exists ?? false,
      orgEmail: paying?.orgEmail ?? null,
      ssoEnforced: sso === true,
    };
    // Only a complete answer is worth caching for the rest of the tab.
    if (paying) cacheExistingOrgCheck(email, check);
  }
  return {decision: resolveOrgGate(check, hasSeparateOrgOptIn(email)), check};
}
